// ✅ MathSprint.jsx - Mental Math Game for BrainyBuddy

import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../../firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

import Navbar from '../../components/Navbar';
import { Toaster, toast } from 'react-hot-toast';
import Confetti from 'react-confetti';
import { useWindowSize } from '@uidotdev/usehooks';
import { useNavigate } from 'react-router-dom';

const generateQuestion = (score) => {
  let grade = Math.floor(score / 10) + 1; // Every 10 questions is a new grade

  let a, b, operations;
  if (grade === 1) {
    a = Math.floor(Math.random() * 10);
    b = Math.floor(Math.random() * 10);
    operations = ['+'];
  } else if (grade === 2) {
    a = Math.floor(Math.random() * 20);
    b = Math.floor(Math.random() * 10);
    operations = ['+', '-'];
  } else if (grade === 3) {
    a = Math.floor(Math.random() * 10);
    b = Math.floor(Math.random() * 10);
    operations = ['+', '-', '*'];
  } else {
    a = Math.floor(Math.random() * 12);
    b = Math.floor(Math.random() * 12);
    operations = ['+', '-', '*']; // Removed division for simplicity
  }

  const op = operations[Math.floor(Math.random() * operations.length)];
  let expression = `${a} ${op} ${b}`;
  let answer = eval(expression);
  return { question: expression, answer };
};


export default function MathSprint() {
  const [loading, setLoading] = useState(true);
  const [questionObj, setQuestionObj] = useState(generateQuestion(1));
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [chronos, setChronos] = useState(0);
  const [playLimit, setPlayLimit] = useState(25);
  const [todayPlays, setTodayPlays] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [timer, setTimer] = useState(10);
  const [combo, setCombo] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const { width, height } = useWindowSize();
  const inputRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    if (timer === 0) {
      setGameOver(true);
      toast.error("⏰ Time's up!");
      return;
    }
    if (gameOver || limitReached) return;
    const countdown = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(countdown);
  }, [timer, gameOver]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          setLoading(false);
          return;
        }
        const data = snap.data();
        const today = new Date().toDateString();

        if (data.mathSprintPlaysDate !== today) {
          await updateDoc(userRef, {
            mathSprintPlays: 0,
            mathSprintPlaysDate: today
          });
          toast.success('📅 New day, new Sprint!');
        }

        // Load Chronos
        setChronos(data.chronos || 0);
        // Determine limit by accessType
        const limit = data.accessType === 'pro' ? 200 : 25;
        setPlayLimit(limit);
        // Calculate plays for today without writes
        const plays = data.mathSprintPlaysDate === today ? data.mathSprintPlays || 0 : 0;
        setTodayPlays(plays);
        setLimitReached(plays >= limit);
      } catch (e) {
        console.error('Error fetching MathSprint data:', e);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (gameOver || limitReached) return;
    const correct = Number(userAnswer) === questionObj.answer;
    if (correct) {
      const bonus = combo >= 3 ? 3 : 2;
      toast.success(`✅ Correct! +${bonus} Chronos`);
      
      const newChronos = chronos + bonus;
      const newPlayCount = todayPlays + 1;
      if (newPlayCount >= playLimit) {
        setLimitReached(true);
        toast.error('🎓 Demo play limit reached!');
        setGameOver(true);
      }
      setTodayPlays(newPlayCount);
      setChronos(newChronos);
      setScore(s => s + 1);
      setCombo(c => c + 1);
      setQuestionObj(generateQuestion(score + 1));
      setUserAnswer('');
      setTimer(Math.max(10 - Math.floor((score + 1) / 5), 4));
      inputRef.current.focus();

      const ref = doc(db, 'users', auth.currentUser.uid);
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() : {};
      await updateDoc(ref, {
        chronos: newChronos,
        mathSprintPlays: newPlayCount,
        mathSprintPlaysDate: new Date().toDateString()
      });

      const lbRef = doc(db, 'leaderboard_master', auth.currentUser.uid);
      const lbSnap = await getDoc(lbRef);
      

      if (lbSnap.exists()) {
        const prev = lbSnap.data();
        await updateDoc(lbRef, {
          name: data.name || 'Brainy Player',
          chronos: newChronos,
          bestMathSprintScore: Math.max(prev.bestMathSprintScore || 0, score + 1),
          bestNumberMemoryLevel: prev.bestNumberMemoryLevel || 0,
          bestFocusGameStreak: prev.bestFocusGameStreak || 0,
          bestSynonymWords: prev.bestSynonymWords || 0,
          lastPlayed: new Date().toDateString()
        });
      } else {
        await setDoc(lbRef, {
          name: data.name || 'Brainy Player',
          chronos: newChronos,
          bestMathSprintScore: score + 1,
          bestNumberMemoryLevel: 0,
          bestFocusGameStreak: 0,
          bestSynonymWords: 0,
          lastPlayed: new Date().toDateString()
        });
      }
      
      
        
  }
  };

  const restart = () => {
    if (limitReached) return toast.error('🛑 Daily limit reached! Come back tomorrow.');
    setScore(0);
    setCombo(0);
    setTimer(10);
    setGameOver(false);
    setQuestionObj(generateQuestion(0));
    setUserAnswer('');
    inputRef.current.focus();
  };

  return (
    <>
      <Navbar />
      <Toaster position="top-center" />
      {gameOver && <Confetti width={width} height={height} />}

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-purple-50 flex flex-col items-center justify-center p-6">
        <h1 className="text-3xl font-bold text-purple-800 mb-2">🧠 Math Sprint</h1>
        <p className="text-purple-600 mb-4">Solve fast and accurately to build your math power!</p>
        <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full text-center space-y-4">
          {limitReached && (
            <div className="text-sm text-red-600 font-semibold bg-red-50 p-2 rounded-md border border-red-300">
              🎯 You've reached today’s Math Sprint limit.<br />Rest well and return tomorrow sharper than ever!
            </div>
          )}
          <div className="text-xl font-semibold text-gray-800">Question:</div>
          <div className="text-3xl font-bold text-purple-700">{questionObj.question}</div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              ref={inputRef}
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              className="text-center text-xl border border-purple-300 rounded-lg w-full p-2"
              type="number" inputMode="numeric" pattern="[0-9]*"
              disabled={gameOver}
              autoFocus
            />
            <button
              type="submit"
              className="w-full bg-purple-600 text-white rounded-lg py-2 hover:bg-purple-700"
              disabled={gameOver}
            >Submit</button>
          </form>

          <div className="text-sm text-gray-600">⏳ Time Left: {timer}s</div>
          <div className="text-sm text-yellow-600">🔥 Combo: {combo}</div>
          <div className="text-sm text-green-600">Chronos: {chronos}</div>
          <div className="text-sm text-blue-600">Score: {score}</div>

          {gameOver && (
            <button
              onClick={restart}
              className="mt-4 w-full bg-green-600 text-white rounded-lg py-2 hover:bg-green-700"
            >🔁 Restart</button>
          )}
        </div>
      </div>
    </>
  );
}
